import { useSelector } from "react-redux";
import { AUTH_USER_TYPES } from "../constants/auth.constants";
import { getTenantLabel } from "../config/loginTenants";
import { pickAccessToken, pickTenantApiToken } from "../helpers/authStorage.helper";
import { resolveUserNames } from "../helpers/userName.helper";

const getDisplayName = (user = {}, fallback = "") => {
  const names = resolveUserNames(user);
  return names.displayName || fallback || "";
};

const getInitials = (user = {}, fallback = "") => {
  const names = resolveUserNames(user);
  const first = String(names.firstname || "").trim();
  const last = String(names.lastname || "").trim();
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }

  const name = getDisplayName(user, fallback);
  const parts = String(name)
    .split(/[\s._-]+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  return String(name || fallback || "U")
    .charAt(0)
    .toUpperCase();
};

export default function useAuth() {
  const auth = useSelector((state) => state?.authentication || {});
  const isSystemUser = auth.userType === AUTH_USER_TYPES.SYSTEM;
  const isTenantUser = auth.userType === AUTH_USER_TYPES.TENANT;
  const tenantLabel = isTenantUser ? getTenantLabel(auth.tenantId) : null;
  const activeTenantId = auth.activeTenantId || (isTenantUser ? auth.tenantId : null);
  const activeTenantName =
    auth.activeTenantName ||
    (isTenantUser ? tenantLabel || auth.tenantId : null);
  const contextLabel = isTenantUser
    ? tenantLabel || "Tenant User"
    : "System User";
  const user = auth.user || {};
  const displayName = getDisplayName(user, contextLabel);
  const initials = getInitials(user, contextLabel);
  const accessToken = isTenantUser
    ? pickTenantApiToken(user) || pickAccessToken(user)
    : pickAccessToken(user) || pickTenantApiToken(user);

  return {
    loggedIn: Boolean(auth.loggedIn),
    loading: Boolean(auth.loading),
    user,
    accessToken: accessToken || null,
    userType: auth.userType || null,
    tenantId: auth.tenantId || null,
    tenantLabel,
    activeTenantId: activeTenantId || null,
    activeTenantName: activeTenantName || null,
    roles: auth.roles || [],
    permissions: auth.permissions || [],
    error: auth.error,
    isSystemUser,
    isTenantUser,
    contextLabel,
    displayName,
    initials,
  };
}
